<template>
  <v-dialog
    v-model="isOpen"
    fullscreen
    hide-overlay
    transition="dialog-bottom-transition"
    persistent
  >
    <v-card class="branch-dte-modal">
      <v-toolbar flat class="branch-dte-modal__toolbar">
        <div class="branch-dte-modal__toolbar-main">
          <div class="branch-dte-modal__toolbar-heading">
            <div class="branch-dte-modal__eyebrow">Configuracion de sucursal</div>
            <v-toolbar-title class="branch-dte-modal__title">
              Plantilla DTE · {{ branchName }}
            </v-toolbar-title>
          </div>

          <v-select
            dense
            outlined
            hide-details
            class="branch-dte-modal__layout-select"
            :items="layoutItems"
            item-text="label"
            item-value="key"
            label="Layout"
            :disabled="loading || restoring || settingDefault || copying"
            v-model="selectedLayoutKey"
          />

          <v-chip
            small
            label
            :color="selectedLayoutIsDefault ? 'success' : 'grey lighten-2'"
            :text-color="selectedLayoutIsDefault ? 'white' : 'grey darken-3'"
          >
            {{
              selectedLayoutIsDefault
                ? "Layout predeterminado"
                : `Predeterminado actual: ${defaultLayoutLabel}`
            }}
          </v-chip>

          <div class="branch-dte-modal__status" :class="`is-${saveStatusTone}`">
            {{ saveStatusLabel }}
          </div>

          <v-checkbox
            v-if="showAutoSaveControl"
            v-model="autoSaveEnabled"
            hide-details
            dense
            class="branch-dte-modal__autosave-toggle"
            label="Autoguardado"
            :disabled="loading || restoring || settingDefault || copying || !canEdit || !branchId"
          ></v-checkbox>
        </div>

        <div class="branch-dte-modal__toolbar-actions">
          <v-btn
            text
            color="primary"
            class="branch-dte-modal__action-btn"
            :disabled="
              loading ||
              restoring ||
              settingDefault ||
              copying ||
              !canEdit ||
              !branchId ||
              autoSaveEnabled ||
              !hasUnsavedChanges
            "
            @click="saveCurrentSettingsManually"
          >
            Guardar
          </v-btn>
          <v-btn
            text
            color="primary"
            class="branch-dte-modal__action-btn"
            :disabled="loading || restoring || settingDefault || copying || !canEdit || !branchId"
            @click="setAsDefault"
          >
            {{ selectedLayoutIsDefault ? "Reafirmar predeterminado" : "Usar como predeterminado" }}
          </v-btn>
          <v-btn
            text
            color="primary"
            class="branch-dte-modal__action-btn"
            :disabled="loading || restoring || settingDefault || copying || !canEdit || !branchId"
            @click="copyToAllBranches"
          >
            Copiar a todas las sucursales
          </v-btn>
          <v-btn
            text
            color="warning"
            class="branch-dte-modal__action-btn"
            :disabled="loading || restoring || settingDefault || copying || !canEdit || !branchId"
            @click="restoreDefaults"
          >
            Restaurar por defecto
          </v-btn>
          <v-btn icon @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </v-toolbar>

      <div class="branch-dte-modal__body">
        <div class="branch-dte-modal__preview">
          <branch-dte-template-preview
            :branch="branch"
            :business="business"
            :layout-key="selectedLayoutKey"
            :settings="currentSettings"
            :preview-assets="previewAssets"
          />
        </div>

        <div class="branch-dte-modal__editor">
          <v-card
            v-if="branchId && canEdit"
            outlined
            class="branch-dte-modal__copy-card mb-4"
          >
            <button
              type="button"
              class="branch-dte-modal__copy-header"
              @click="copyAccordionOpen = !copyAccordionOpen"
            >
              <span class="branch-dte-modal__copy-title">
                Aplicar plantilla desde otra sucursal
              </span>
              <v-icon small>
                {{ copyAccordionOpen ? "mdi-chevron-up" : "mdi-chevron-down" }}
              </v-icon>
            </button>

            <v-expand-transition>
              <div v-show="copyAccordionOpen">
                <v-card-text class="pb-2">
                  <v-row dense align="center">
                    <v-col cols="12" md="12" class="mb-4">
                      <v-select
                        dense
                        outlined
                        hide-details
                        clearable
                        :disabled="loading || restoring || copyingFromBranch"
                        :items="copySourceBranchItems"
                        item-text="label"
                        item-value="value"
                        label="Sucursal origen"
                        v-model="selectedSourceBranchId"
                      />
                    </v-col>
                    <v-col cols="12" md="12">
                      <v-btn
                        block
                        class="btn-save"
                        :disabled="loading || restoring || copyingFromBranch || !selectedSourceBranchId"
                        @click="copyFromBranch"
                      >
                        Copiar plantilla
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </div>
            </v-expand-transition>
          </v-card>

          <v-alert
            v-if="infoMessage"
            dense
            outlined
            type="success"
            class="mb-4"
          >
            {{ infoMessage }}
          </v-alert>

          <v-alert
            v-if="!branchId"
            dense
            outlined
            type="info"
            class="mb-4"
          >
            La plantilla DTE se habilita cuando la sucursal ya existe y tiene ID asignado.
          </v-alert>

          <v-alert
            v-else-if="validationError"
            dense
            outlined
            type="error"
            class="mb-4"
          >
            {{ validationError }}
          </v-alert>

          <v-alert
            v-else-if="saveError"
            dense
            outlined
            type="error"
            class="mb-4"
          >
            {{ saveError }}
          </v-alert>

          <branch-dte-template-editor-panel
            :settings="currentSettings"
            :layout-key="selectedLayoutKey"
            :disabled="loading || restoring || !canEdit || !branchId"
            @input="updateCurrentSettings"
          />
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import BranchDteTemplateEditorPanel from "./BranchDteTemplateEditorPanel.vue";
import BranchDteTemplatePreview from "./BranchDteTemplatePreview.vue";
import {
  DEFAULT_BRANCH_DTE_TEMPLATE_LAYOUT_KEY,
  DTE_TEMPLATE_LAYOUT_OPTIONS,
  getDefaultDteTemplateSettings,
  safeResolveDteTemplateSettings,
  validateAndResolveDteTemplateSettings,
} from "@/helpers/dteTemplateSettings";

export default {
  name: "BranchDteTemplateSettingsModal",
  components: {
    BranchDteTemplateEditorPanel,
    BranchDteTemplatePreview,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    branch: {
      type: Object,
      default: () => ({}),
    },
    business: {
      type: Object,
      default: () => ({}),
    },
    previewAssets: {
      type: Object,
      default: () => ({}),
    },
    canEdit: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isOpen: false,
      selectedLayoutKey: DEFAULT_BRANCH_DTE_TEMPLATE_LAYOUT_KEY,
      defaultLayoutKey: "",
      layoutItems: DTE_TEMPLATE_LAYOUT_OPTIONS,
      settingsByLayout: {},
      lastSavedByLayout: {},
      loadedLayouts: {},
      availableBranches: [],
      selectedSourceBranchId: null,
      copyAccordionOpen: false,
      loading: false,
      restoring: false,
      settingDefault: false,
      copying: false,
      copyingFromBranch: false,
      saveStatus: "idle",
      validationError: "",
      saveError: "",
      infoMessage: "",
      debounceTimer: null,
      isPersisting: false,
      autoSaveEnabled: false,
    };
  },
  computed: {
    branchId() {
      return this.branch && this.branch.ID_Branch ? this.branch.ID_Branch : null;
    },
    branchName() {
      return (
        this.branch.BranchName ||
        this.branch.BranchLegalName ||
        "Sucursal"
      );
    },
    currentSettings() {
      return (
        this.settingsByLayout[this.selectedLayoutKey] ||
        getDefaultDteTemplateSettings(this.selectedLayoutKey)
      );
    },
    businessId() {
      return this.business && this.business.ID_Business
        ? this.business.ID_Business
        : null;
    },
    copySourceBranchItems() {
      return this.availableBranches
        .filter((item) => Number(item.ID_Branch) !== Number(this.branchId))
        .map((item) => ({
          value: item.ID_Branch,
          label: item.BranchName || item.BranchLegalName || `Sucursal ${item.ID_Branch}`,
        }));
    },
    selectedLayoutIsDefault() {
      return this.selectedLayoutKey === this.defaultLayoutKey;
    },
    defaultLayoutLabel() {
      if (!this.defaultLayoutKey) {
        return "Sin plantilla predeterminada";
      }
      const matched = this.layoutItems.find(
        (item) => item.key === this.defaultLayoutKey
      );
      return matched ? matched.label : "Sin plantilla predeterminada";
    },
    hasUnsavedChanges() {
      if (!this.branchId || !this.loadedLayouts[this.selectedLayoutKey]) {
        return false;
      }

      const currentSnapshot = JSON.stringify(
        safeResolveDteTemplateSettings(
          this.settingsByLayout[this.selectedLayoutKey] || {},
          this.selectedLayoutKey
        )
      );

      return currentSnapshot !== this.lastSavedByLayout[this.selectedLayoutKey];
    },
    showAutoSaveControl() {
      return String(process.env.VUE_APP_ENV || "").trim() === "1";
    },
    saveStatusTone() {
      if (this.saveStatus === "error") return "error";
      if (!this.autoSaveEnabled && this.hasUnsavedChanges) return "pending";
      if (this.saveStatus === "saving") return "saving";
      if (this.saveStatus === "pending") return "pending";
      return "neutral";
    },
    saveStatusLabel() {
      if (!this.branchId) {
        return "Disponible al guardar la sucursal";
      }
      if (this.loading) {
        return "Cargando configuracion...";
      }
      if (this.restoring) {
        return "Restaurando valores...";
      }
      if (this.copyingFromBranch) {
        return "Copiando plantilla desde sucursal...";
      }
      if (this.settingDefault) {
        return "Actualizando layout predeterminado...";
      }
      if (this.copying) {
        return "Copiando configuracion a sucursales...";
      }
      if (!this.autoSaveEnabled && this.hasUnsavedChanges) {
        return "Cambios sin guardar";
      }
      if (this.saveStatus === "saving") {
        return "Guardando cambios...";
      }
      if (this.saveStatus === "pending") {
        return "Cambios pendientes por guardar";
      }
      if (this.saveStatus === "saved") {
        return this.autoSaveEnabled
          ? "Guardado automaticamente"
          : "Guardado manualmente";
      }
      if (this.saveStatus === "error") {
        return "Revisar cambios";
      }
      return "Edicion lista";
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(value) {
        this.isOpen = value;
        if (value) {
          this.bootstrap();
        } else {
          this.clearDebounce();
        }
      },
    },
    isOpen(value) {
      this.$emit("input", value);
    },
    selectedLayoutKey() {
      if (this.isOpen) {
        this.ensureLayoutLoaded(this.selectedLayoutKey);
      }
    },
    branchId() {
      if (this.isOpen) {
        this.bootstrap();
      }
    },
    autoSaveEnabled(value) {
      if (!value) {
        this.clearDebounce();
        return;
      }

      if (!this.branchId || !this.hasUnsavedChanges || this.validationError) {
        return;
      }

      const resolvedSettings = safeResolveDteTemplateSettings(
        this.settingsByLayout[this.selectedLayoutKey] || {},
        this.selectedLayoutKey
      );
      this.scheduleSave(this.selectedLayoutKey, resolvedSettings);
    },
  },
  methods: {
    getInitialLayoutKey() {
      const candidate = String(
        this.defaultLayoutKey ||
          this.branch.DteTemplateDefaultLayoutKey ||
          DEFAULT_BRANCH_DTE_TEMPLATE_LAYOUT_KEY
      )
        .trim()
        .toLowerCase();
      const allowedKeys = this.layoutItems.map((item) => item.key);
      return allowedKeys.includes(candidate)
        ? candidate
        : DEFAULT_BRANCH_DTE_TEMPLATE_LAYOUT_KEY;
    },
    clearDebounce() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
      }
    },
    close() {
      this.isOpen = false;
    },
    async bootstrap() {
      this.clearDebounce();
      this.validationError = "";
      this.saveError = "";
      this.infoMessage = "";
      this.saveStatus = "idle";
      this.settingsByLayout = {};
      this.lastSavedByLayout = {};
      this.loadedLayouts = {};
      this.availableBranches = [];
      this.selectedSourceBranchId = null;
      this.layoutItems = DTE_TEMPLATE_LAYOUT_OPTIONS;
      this.defaultLayoutKey = this.branch.DteTemplateDefaultLayoutKey || "";
      this.selectedLayoutKey = this.getInitialLayoutKey();

      if (!this.branchId) {
        this.$set(
          this.settingsByLayout,
          this.selectedLayoutKey,
          getDefaultDteTemplateSettings(this.selectedLayoutKey)
        );
        return;
      }

      this.loading = true;

      try {
        const [layoutsResponse, branchesResponse] = await Promise.all([
          this.$API.branchDteTemplateSettings.getLayouts(),
          this.businessId
            ? this.$API.branches.findBranchesHistory({ ID_Business: this.businessId })
            : Promise.resolve([]),
        ]);
        if (layoutsResponse && layoutsResponse.success && layoutsResponse.data.layouts) {
          this.layoutItems = layoutsResponse.data.layouts.map((layout) => ({
            key: layout.key,
            label: layout.label,
          }));
          this.selectedLayoutKey = this.getInitialLayoutKey();
        }
        this.availableBranches = Array.isArray(branchesResponse) ? branchesResponse : [];
      } catch (error) {
        this.layoutItems = DTE_TEMPLATE_LAYOUT_OPTIONS;
        this.availableBranches = [];
      } finally {
        this.loading = false;
      }

      await this.ensureLayoutLoaded(this.selectedLayoutKey);
    },
    async ensureLayoutLoaded(layoutKey) {
      if (this.loadedLayouts[layoutKey]) {
        return;
      }

      if (!this.branchId) {
        this.$set(this.settingsByLayout, layoutKey, getDefaultDteTemplateSettings(layoutKey));
        this.$set(this.lastSavedByLayout, layoutKey, JSON.stringify(this.settingsByLayout[layoutKey]));
        this.$set(this.loadedLayouts, layoutKey, true);
        return;
      }

      this.loading = true;
      this.validationError = "";
      this.saveError = "";
      this.infoMessage = "";

      try {
        const response = await this.$API.branchDteTemplateSettings.getSettings(
          this.branchId,
          layoutKey
        );
        if (
          response &&
          response.data &&
          Object.prototype.hasOwnProperty.call(response.data, "defaultLayoutKey")
        ) {
          this.defaultLayoutKey = response.data.defaultLayoutKey || "";
        }
        const settings = safeResolveDteTemplateSettings(
          response && response.data ? response.data.settings : {},
          layoutKey
        );
        this.$set(this.settingsByLayout, layoutKey, settings);
        this.$set(this.lastSavedByLayout, layoutKey, JSON.stringify(settings));
        this.$set(this.loadedLayouts, layoutKey, true);
      } catch (error) {
        const settings = getDefaultDteTemplateSettings(layoutKey);
        this.$set(this.settingsByLayout, layoutKey, settings);
        this.$set(this.lastSavedByLayout, layoutKey, JSON.stringify(settings));
        this.$set(this.loadedLayouts, layoutKey, true);
        this.saveError =
          error?.response?.data?.error ||
          "No se pudo cargar la configuracion guardada. Se usaron valores por defecto.";
      } finally {
        this.loading = false;
      }
    },
    updateCurrentSettings(nextSettings) {
      this.$set(this.settingsByLayout, this.selectedLayoutKey, nextSettings);
      this.saveError = "";
      this.infoMessage = "";

      try {
        const resolvedSettings = validateAndResolveDteTemplateSettings(
          nextSettings,
          this.selectedLayoutKey
        );
        this.validationError = "";
        this.$set(this.settingsByLayout, this.selectedLayoutKey, resolvedSettings);
        if (this.autoSaveEnabled) {
          this.scheduleSave(this.selectedLayoutKey, resolvedSettings);
        } else {
          const snapshot = JSON.stringify(resolvedSettings);
          this.saveStatus =
            snapshot === this.lastSavedByLayout[this.selectedLayoutKey]
              ? "saved"
              : "pending";
        }
      } catch (error) {
        this.validationError = error.message;
        this.saveStatus = "error";
      }
    },
    resolveCurrentLayoutSettings() {
      const resolvedSettings = validateAndResolveDteTemplateSettings(
        this.currentSettings,
        this.selectedLayoutKey
      );
      this.validationError = "";
      this.$set(this.settingsByLayout, this.selectedLayoutKey, resolvedSettings);
      return resolvedSettings;
    },
    scheduleSave(layoutKey, resolvedSettings) {
      if (!this.canEdit || !this.branchId || !this.autoSaveEnabled) {
        return;
      }

      const snapshot = JSON.stringify(resolvedSettings);
      if (snapshot === this.lastSavedByLayout[layoutKey]) {
        this.saveStatus = "saved";
        return;
      }

      this.clearDebounce();
      this.saveStatus = "pending";
      this.debounceTimer = setTimeout(() => {
        this.flushPendingSave(layoutKey);
      }, 1800);
    },
    flushPendingSave(layoutKey) {
      if (!this.canEdit || !this.branchId) {
        return;
      }

      if (this.isPersisting) {
        return;
      }

      const currentSettings = safeResolveDteTemplateSettings(
        this.settingsByLayout[layoutKey] || {},
        layoutKey
      );
      const snapshot = JSON.stringify(currentSettings);

      if (snapshot === this.lastSavedByLayout[layoutKey]) {
        this.saveStatus = "saved";
        return;
      }

      this.persistSettings(layoutKey, currentSettings, snapshot);
    },
    async persistSettings(layoutKey, resolvedSettings, requestSnapshot) {
      this.isPersisting = true;
      this.saveStatus = "saving";

      try {
        const response = await this.$API.branchDteTemplateSettings.saveSettings(
          this.branchId,
          layoutKey,
          resolvedSettings
        );
        const savedSettings = safeResolveDteTemplateSettings(
          response && response.data ? response.data.settings : resolvedSettings,
          layoutKey
        );
        const savedSnapshot = JSON.stringify(savedSettings);
        const currentDraft = safeResolveDteTemplateSettings(
          this.settingsByLayout[layoutKey] || {},
          layoutKey
        );
        const currentSnapshot = JSON.stringify(currentDraft);

        this.$set(this.lastSavedByLayout, layoutKey, savedSnapshot);

        if (
          currentSnapshot === requestSnapshot ||
          currentSnapshot === savedSnapshot
        ) {
          this.$set(this.settingsByLayout, layoutKey, savedSettings);
          this.saveStatus = "saved";
        } else {
          this.saveStatus = "pending";
        }
        this.validationError = "";
        this.saveError = "";
      } catch (error) {
        this.saveStatus = "error";
        this.saveError =
          error?.response?.data?.error ||
          "No se pudieron guardar los cambios.";
      } finally {
        this.isPersisting = false;
      }

      const latestSettings = safeResolveDteTemplateSettings(
        this.settingsByLayout[layoutKey] || {},
        layoutKey
      );
      const latestSnapshot = JSON.stringify(latestSettings);

      if (
        this.canEdit &&
        this.branchId &&
        this.autoSaveEnabled &&
        this.saveStatus !== "error" &&
        latestSnapshot !== this.lastSavedByLayout[layoutKey]
      ) {
        this.scheduleSave(layoutKey, latestSettings);
      }
    },
    async saveCurrentSettingsManually() {
      if (
        !this.branchId ||
        !this.canEdit ||
        this.loading ||
        this.restoring ||
        this.settingDefault ||
        this.copying ||
        this.autoSaveEnabled
      ) {
        return;
      }

      this.clearDebounce();
      this.saveError = "";
      this.infoMessage = "";

      let resolvedSettings;
      try {
        resolvedSettings = this.resolveCurrentLayoutSettings();
      } catch (error) {
        this.validationError = error.message;
        this.saveStatus = "error";
        return;
      }

      const snapshot = JSON.stringify(resolvedSettings);
      if (snapshot === this.lastSavedByLayout[this.selectedLayoutKey]) {
        this.saveStatus = "saved";
        return;
      }

      await this.persistSettings(this.selectedLayoutKey, resolvedSettings, snapshot);
    },
    async setAsDefault() {
      if (!this.branchId || !this.canEdit || this.settingDefault) {
        return;
      }

      this.clearDebounce();
      this.saveError = "";
      this.infoMessage = "";

      let resolvedSettings;
      try {
        resolvedSettings = this.resolveCurrentLayoutSettings();
      } catch (error) {
        this.validationError = error.message;
        this.saveStatus = "error";
        return;
      }

      this.settingDefault = true;

      try {
        const response = await this.$API.branchDteTemplateSettings.setDefaultLayout(
          this.branchId,
          this.selectedLayoutKey,
          resolvedSettings
        );
        const savedSettings = safeResolveDteTemplateSettings(
          response && response.data ? response.data.settings : resolvedSettings,
          this.selectedLayoutKey
        );
        const snapshot = JSON.stringify(savedSettings);
        this.$set(this.settingsByLayout, this.selectedLayoutKey, savedSettings);
        this.$set(this.lastSavedByLayout, this.selectedLayoutKey, snapshot);
        this.defaultLayoutKey =
          response && response.data && response.data.defaultLayoutKey
            ? response.data.defaultLayoutKey
            : this.selectedLayoutKey;
        this.saveStatus = "saved";
        this.validationError = "";
        this.saveError = "";
        this.infoMessage = "El layout seleccionado ya quedo como predeterminado.";
        this.$emit("default-layout-updated", {
          branchId: this.branchId,
          defaultLayoutKey: this.defaultLayoutKey,
          useTemplateV2: 1,
        });
      } catch (error) {
        this.saveStatus = "error";
        this.saveError =
          error?.response?.data?.error ||
          "No se pudo actualizar el layout predeterminado.";
      } finally {
        this.settingDefault = false;
      }
    },
    async copyToAllBranches() {
      if (!this.branchId || !this.canEdit || this.copying) {
        return;
      }

      const confirmed = window.confirm(
        "Se copiara este layout y su configuracion a todas las sucursales del negocio. Desea continuar?"
      );

      if (!confirmed) {
        return;
      }

      this.clearDebounce();
      this.saveError = "";
      this.infoMessage = "";

      let resolvedSettings;
      try {
        resolvedSettings = this.resolveCurrentLayoutSettings();
      } catch (error) {
        this.validationError = error.message;
        this.saveStatus = "error";
        return;
      }

      this.copying = true;

      try {
        const response = await this.$API.branchDteTemplateSettings.copyLayoutToAllBranches(
          this.branchId,
          this.selectedLayoutKey,
          resolvedSettings
        );
        const savedSettings = safeResolveDteTemplateSettings(
          response && response.data ? response.data.settings : resolvedSettings,
          this.selectedLayoutKey
        );
        const snapshot = JSON.stringify(savedSettings);
        this.$set(this.settingsByLayout, this.selectedLayoutKey, savedSettings);
        this.$set(this.lastSavedByLayout, this.selectedLayoutKey, snapshot);
        this.defaultLayoutKey =
          response && response.data && response.data.defaultLayoutKey
            ? response.data.defaultLayoutKey
            : this.selectedLayoutKey;
        this.saveStatus = "saved";
        this.validationError = "";
        this.saveError = "";
        const copiedCount =
          response && response.data && response.data.copiedBranchesCount
            ? response.data.copiedBranchesCount
            : 0;
        this.infoMessage =
          copiedCount > 0
            ? `Se copio el layout a ${copiedCount} sucursales.`
            : "Se copio el layout a las sucursales del negocio.";
        this.$emit("default-layout-updated", {
          branchId: this.branchId,
          defaultLayoutKey: this.defaultLayoutKey,
          useTemplateV2: 1,
          copiedToAllBranches: true,
        });
      } catch (error) {
        this.saveStatus = "error";
        this.saveError =
          error?.response?.data?.error ||
          "No se pudo copiar el layout al resto de sucursales.";
      } finally {
        this.copying = false;
      }
    },
    async restoreDefaults() {
      const layoutKey = this.selectedLayoutKey;
      this.clearDebounce();
      this.validationError = "";
      this.saveError = "";
      this.infoMessage = "";

      if (!this.branchId || !this.canEdit) {
        const defaults = getDefaultDteTemplateSettings(layoutKey);
        this.$set(this.settingsByLayout, layoutKey, defaults);
        this.$set(this.lastSavedByLayout, layoutKey, JSON.stringify(defaults));
        this.saveStatus = "saved";
        return;
      }

      this.restoring = true;

      try {
        const response = await this.$API.branchDteTemplateSettings.resetSettings(
          this.branchId,
          layoutKey
        );
        const defaults = safeResolveDteTemplateSettings(
          response && response.data ? response.data.settings : {},
          layoutKey
        );
        this.$set(this.settingsByLayout, layoutKey, defaults);
        this.$set(this.lastSavedByLayout, layoutKey, JSON.stringify(defaults));
        this.saveStatus = "saved";
      } catch (error) {
        this.saveStatus = "error";
        this.saveError =
          error?.response?.data?.error ||
          "No se pudo restaurar la plantilla por defecto.";
      } finally {
        this.restoring = false;
      }
    },
    async copyFromBranch() {
      if (
        !this.branchId ||
        !this.canEdit ||
        !this.selectedSourceBranchId ||
        this.copyingFromBranch
      ) {
        return;
      }

      this.clearDebounce();
      this.validationError = "";
      this.saveError = "";
      this.infoMessage = "";
      this.copyingFromBranch = true;

      try {
        const sourceResponse = await this.$API.branchDteTemplateSettings.getSettings(
          this.selectedSourceBranchId,
          this.selectedLayoutKey
        );
        const sourceSettings = safeResolveDteTemplateSettings(
          sourceResponse && sourceResponse.data ? sourceResponse.data.settings : {},
          this.selectedLayoutKey
        );

        const saveResponse = await this.$API.branchDteTemplateSettings.saveSettings(
          this.branchId,
          this.selectedLayoutKey,
          sourceSettings
        );

        const savedSettings = safeResolveDteTemplateSettings(
          saveResponse && saveResponse.data ? saveResponse.data.settings : sourceSettings,
          this.selectedLayoutKey
        );
        const snapshot = JSON.stringify(savedSettings);

        this.$set(this.settingsByLayout, this.selectedLayoutKey, savedSettings);
        this.$set(this.lastSavedByLayout, this.selectedLayoutKey, snapshot);
        this.$set(this.loadedLayouts, this.selectedLayoutKey, true);
        this.saveStatus = "saved";

        const sourceBranch = this.availableBranches.find(
          (item) => Number(item.ID_Branch) === Number(this.selectedSourceBranchId)
        );
        const sourceBranchName = sourceBranch
          ? sourceBranch.BranchName || sourceBranch.BranchLegalName || `Sucursal ${sourceBranch.ID_Branch}`
          : `Sucursal ${this.selectedSourceBranchId}`;

        this.infoMessage = `Se copio la plantilla de ${sourceBranchName}.`;
      } catch (error) {
        this.saveStatus = "error";
        this.saveError =
          error?.response?.data?.error ||
          "No se pudo copiar la plantilla desde la sucursal seleccionada.";
      } finally {
        this.copyingFromBranch = false;
      }
    },
  },
};
</script>

<style scoped>
.branch-dte-modal {
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  height: 100vh;
  background: #f3f6fb;
}

.branch-dte-modal__toolbar {
  border-bottom: 1px solid #d8e0ea;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(10px);
  height: auto !important;
}

.branch-dte-modal__toolbar-main {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  flex: 1 1 560px;
  min-width: 0;
}

.branch-dte-modal__toolbar-heading {
  min-width: 220px;
}

.branch-dte-modal__toolbar-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
  flex: 1 1 360px;
}

.branch-dte-modal__action-btn {
  white-space: normal;
  max-width: 100%;
}

.branch-dte-modal__eyebrow {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64798e;
}

.branch-dte-modal__title {
  padding: 0;
  font-size: 1.1rem;
  color: #1f3347;
}

.branch-dte-modal__layout-select {
  min-width: 190px;
  max-width: 220px;
}

.branch-dte-modal__status {
  min-width: 220px;
  padding: 8px 12px;
  border-radius: 999px;
  background: #e9eef5;
  color: #3f556b;
  font-size: 0.82rem;
  font-weight: 600;
  text-align: center;
}

.branch-dte-modal__autosave-toggle {
  margin: 0;
  padding: 0;
}

.branch-dte-modal__status.is-saving {
  background: #e6f0ff;
  color: #2450a6;
}

.branch-dte-modal__status.is-pending {
  background: #fff4de;
  color: #946200;
}

.branch-dte-modal__status.is-error {
  background: #fde9e9;
  color: #b73535;
}

::v-deep .branch-dte-modal__toolbar .v-toolbar__content {
  height: auto !important;
  min-height: 64px;
  padding: 12px 16px;
  align-items: flex-start;
  flex-wrap: wrap;
  row-gap: 12px;
}

.branch-dte-modal__body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 420px;
  min-height: 0;
}

.branch-dte-modal__copy-card {
  background: #ffffff;
}

.branch-dte-modal__copy-header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.branch-dte-modal__copy-title {
  margin-bottom: 12px;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f3347;
  margin-bottom: 0;
  text-align: left;
}

.branch-dte-modal__preview,
.branch-dte-modal__editor {
  min-height: 0;
  overflow: auto;
}

.branch-dte-modal__editor {
  padding: 20px;
  border-left: 1px solid #d8e0ea;
  background: #fbfcfe;
}

@media (max-width: 1200px) {
  .branch-dte-modal__body {
    grid-template-columns: minmax(0, 1fr) 360px;
  }
}

@media (max-width: 960px) {
  .branch-dte-modal__toolbar-main,
  .branch-dte-modal__toolbar-actions {
    flex-basis: 100%;
  }

  .branch-dte-modal__toolbar-actions {
    justify-content: flex-start;
  }

  .branch-dte-modal__layout-select,
  .branch-dte-modal__status {
    min-width: 100%;
    max-width: 100%;
  }

  .branch-dte-modal__body {
    grid-template-columns: 1fr;
    grid-template-rows: minmax(0, 1fr) auto;
  }

  .branch-dte-modal__editor {
    border-left: none;
    border-top: 1px solid #d8e0ea;
  }
}
</style>
